
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle } from "lucide-react";
import { useState, useEffect } from "react";
import { useGolfBallDistance } from "@/hooks/useGolfBallDistance";
import { useShotHistory } from "@/hooks/useShotHistory";
import ShotHistory from "./ShotHistory";
import ShotStatistics from "./ShotStatistics";
import { PermissionDialog } from "./dialogs/PermissionDialog";
import { SaveConfirmDialog } from "./dialogs/SaveConfirmDialog";
import { PositionDisplay } from "./tracking/PositionDisplay";
import { TrackingControls } from "./tracking/TrackingControls";
import { GeolocationUnavailable } from "./tracking/GeolocationUnavailable";

export const BallDistanceTracker = () => {
  const { t } = useTranslation('common');
  const {
    isGeolocationAvailable,
    permissionGranted,
    startPosition,
    endPosition,
    progress,
    distance,
    isTracking,
    error,
    startTracking,
    stopTracking,
    resetTracking
  } = useGolfBallDistance();

  const {
    history,
    isLoading,
    saveShot,
    deleteShot,
    loadHistory
  } = useShotHistory();

  const [showPermissionDialog, setShowPermissionDialog] = useState(false);
  const [progressPercent, setProgressPercent] = useState(0);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [geolocationTested, setGeolocationTested] = useState(false);
  
  // Update progress percentage for visual display
  useEffect(() => {
    if (isTracking && distance !== null) {
      // Simulate progress based on distance (max 100m for 100%)
      const maxExpectedDistance = 100; // 100 meters for 100%
      const percent = Math.min(Math.round((distance / maxExpectedDistance) * 100), 100);
      setProgressPercent(percent);
    } else if (!isTracking) {
      setProgressPercent(0);
    }
  }, [isTracking, distance]);

  // Handle start button click
  const handleStartClick = () => {
    setGeolocationTested(true);
    
    // If we don't know permission status yet or it's been denied,
    // show the permission dialog
    if (permissionGranted === false) {
      setShowPermissionDialog(true);
    } else {
      // Otherwise, start tracking normally
      startTracking();
    }
  };

  // Handle saving shot to history
  const handleSaveShot = async (tendency: 'left' | 'right' | null, club?: string, notes?: string) => {
    if (startPosition && endPosition && distance !== null) {
      const success = await saveShot(startPosition, endPosition, distance, tendency, club, notes);
      if (success) {
        setShowSaveConfirm(false);
        // No need to reset as user might want to have the current data visible
      }
    }
  };

  // If geolocation has been tested and isn't available, show error message
  if (geolocationTested && !isGeolocationAvailable) {
    return <GeolocationUnavailable />;
  }

  return (
    <>
      <Card className="w-full max-w-md mx-auto">
        <CardHeader>
          <CardTitle className="text-center">{t('ballDistance.cardTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>{t('ballDistance.error')}</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            {/* Start position */}
            <PositionDisplay 
              type="start" 
              position={startPosition} 
            />
            
            {/* Current tracking position */}
            <PositionDisplay 
              type="current" 
              position={progress} 
              isTracking={isTracking}
              progressPercent={progressPercent}
              distance={distance}
            />
            
            {/* End position and result */}
            <PositionDisplay 
              type="end" 
              position={endPosition} 
              isTracking={isTracking}
              distance={distance}
            />

            {/* Tracking controls */}
            <TrackingControls 
              isTracking={isTracking}
              startTracking={handleStartClick}
              stopTracking={stopTracking}
              resetTracking={resetTracking}
              onSaveClick={() => setShowSaveConfirm(true)}
              startPosition={startPosition}
              endPosition={endPosition}
              distance={distance}
            />
          </div>

          {/* Permission dialog */}
          <PermissionDialog 
            open={showPermissionDialog} 
            onOpenChange={setShowPermissionDialog} 
            onConfirm={startTracking}
          />
          
          {/* Save confirmation dialog */}
          <SaveConfirmDialog 
            open={showSaveConfirm} 
            onOpenChange={setShowSaveConfirm}
            onConfirm={handleSaveShot}
            distance={distance}
          />
        </CardContent>
      </Card>
      
      {/* Shot Statistics Component */}
      <ShotStatistics history={history} />
      
      {/* Shot History Component */}
      <ShotHistory 
        history={history}
        isLoading={isLoading}
        onDelete={deleteShot}
        refreshHistory={loadHistory}
      />
    </>
  );
};

export default BallDistanceTracker;
